<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type { Item } from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type { DidDetail } from '@/apis/generated/dialpad-contact';
import { DATA_QA } from './caller-id-select.constants';
import logger from '@/logger';

const log = logger.getChild('CallerIdSelect');

type Props = {
  didsDetails: DidDetail[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  required: false,
});

const model = defineModel<DidDetail | null>({
  default: null,
});

const emit = defineEmits<(e: 'update:error', value: string | null) => void>();

const modelValue = ref<Item<DidDetail> | null>(null);
const filter = ref('');

const didDetailItemFactory = (didDetail: DidDetail): Item<DidDetail> =>
  ({
    key: didDetail.id,
    title: didDetail.display,
    subtitle: didDetail.label,
    ref: didDetail,
  } as Item<DidDetail>);

const groups = computed(() => {
  return [
    {
      items: props.didsDetails
        .filter((didDetail) => {
          return (
            !!didDetail.display
              ?.toLowerCase()
              .includes(filter.value.toLowerCase()) ||
            !!didDetail.number
              ?.toLowerCase()
              .includes(filter.value.toLowerCase())
          );
        })
        .map(didDetailItemFactory),
    },
  ];
});

watch(
  [model],
  () => {
    if (model.value === modelValue.value?.ref) return;
    log.debug('Model changed', model.value);
    modelValue.value = model.value ? didDetailItemFactory(model.value) : null;
  },
  { immediate: true },
);

watch([modelValue], () => {
  if (model.value === modelValue.value?.ref) return;
  log.debug('ModelValue changed, updating model', modelValue.value);
  model.value = modelValue.value?.ref ?? null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="modelValue"
      :disabled="disabled"
      :label="label"
      :required="required"
      :debounce="0"
      :messages="$ta('CALLER_ID_SELECT_MESSAGES')"
      :list-aria-label="$t('CALLER_ID_SELECT_ARIA_LABEL')"
      :placeholder="$t('CALLER_ID_SELECT_PLACEHOLDER')"
      :error-messages="$ta('CALLER_ID_SELECT_ERROR_MESSAGES')"
      :groups="groups"
      @update:search="(value) => (filter = value)"
      @update:error="(value) => emit('update:error', value)"
    />
  </div>
</template>
